<template>
  <div>
    <!-- Tela normal: filtros + Executar -->
    <div v-if="!props.printMode" class="mb-3">
      <div class="row g-3 align-items-end">
        <!-- Critério -->
        <div class="col-12 col-md-4">
          <label class="form-label d-block">Critério</label>
          <div class="segmented">
            <button
              class="seg-btn"
              :class="{ active: criterio==='realizaram' }"
              @click="criterio='realizaram'"
              title="Alunos que realizaram a avaliação"
            >
              Realizaram
            </button>
            <button
              class="seg-btn"
              :class="{ active: criterio==='nao-realizaram' }"
              @click="criterio='nao-realizaram'"
              title="Alunos que não realizaram a avaliação"
            >
              Não realizaram
            </button>
          </div>
        </div>

        <!-- Avaliação -->
        <div class="col-12 col-md-6">
          <label class="form-label">Avaliação</label>
          <select class="form-select" v-model="avaliacaoId">
            <option v-for="a in avaliacoes" :key="a.id" :value="a.id">{{ a.nome }}</option>
          </select>
        </div>

        <div class="col-12 col-md-2 text-center text-md-start">
          <button class="btn btn-primary w-100" :disabled="!avaliacaoId" @click="abrirPrint">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- Modo impressão: relatório -->
    <div v-else>
      <p v-if="route.query?.subtitle" class="text-center" style="font-size:14px;">
          {{ route.query.subtitle }}
      </p>

      <!-- Resumo por Instrutor (PRIMEIRA TABELA → vira 1ª aba no XLSX) -->
      <div class="mb-3">
        <h6 class="mb-2">Resumo por Instrutor</h6>
        <table class="table table-striped">
            <thead>
            <tr>
                <th>Instrutor</th>
                <th style="width:130px;">Quantidade</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(r, i) in resumoInstrutores.linhas" :key="'resumo-' + i">
                <td>{{ r.instrutor }}</td>
                <td>{{ r.quantidade }}</td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <td><strong>Total</strong></td>
                <td><strong>{{ resumoInstrutores.total }}</strong></td>
            </tr>
            </tfoot>
        </table>
      </div>

      <div v-if="loading" class="text-center">Carregando…</div>
      
      <div v-else>
        <div v-if="Object.keys(data).length === 0" class="text-center">Sem resultados.</div>

        <div v-for="(linhas, instrutor) in data" :key="instrutor" class="mb-4">
          <h6 class="mb-2">Instrutor: {{ instrutor }}</h6>
          <table class="table table-striped">
            <thead>
              <tr>
                <th style="width:90px;">Mat.</th>
                <th>Nome</th>
                <th style="width:110px;">Turma</th>
                <th style="width:160px;">Nível</th>
                <th style="width:130px;">Desde</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in linhas" :key="i">
                <td>{{ row.matricula_id ?? '—' }}</td>
                <td>{{ row.aluno_nome }}</td>
                <td>{{ row.turma_id }}</td>
                <td>{{ row.nivel }}</td>
                <td>{{ row.desde ? formatar(row.desde) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'

const props = defineProps({
  printMode: Boolean,
  printTitle: String
})

const router = useRouter()
const route = useRoute()

// Filtros (tela normal)
const criterio = ref('realizaram')
const avaliacoes = ref([])
const avaliacaoId = ref(null)

// Dados (print)
const loading = ref(false)
const data = ref({})

/** Resumo por instrutor + total geral */
const resumoInstrutores = computed(() => {
  const linhas = []
  let total = 0
  for (const [instr, arr] of Object.entries(data.value || {})) {
    const qtd = Array.isArray(arr) ? arr.length : 0
    linhas.push({ instrutor: instr, quantidade: qtd })
    total += qtd
  }
  // opcional: ordenar por quantidade desc, depois nome
  linhas.sort((a, b) => (b.quantidade - a.quantidade) || a.instrutor.localeCompare(b.instrutor))
  return { linhas, total }
})

function formatar(d) {
  const dt = new Date(d)
  const dd = String(dt.getDate()).padStart(2,'0')
  const mm = String(dt.getMonth()+1).padStart(2,'0')
  const yy = dt.getFullYear()
  return `${dd}/${mm}/${yy}`
}

async function carregarAvaliacoes() {
  try {
    const { data } = await axios.get('/gestao/relatorios/operacionais/avaliacao/periodos-aplicados')
    avaliacoes.value = data || []
    if (avaliacoes.value.length && !avaliacaoId.value) {
      avaliacaoId.value = avaliacoes.value[0].id
    }
  } catch (e) {
    console.error('Falha ao listar avaliações aplicadas:', e)
  }
}

function abrirPrint() {
    if (!avaliacaoId.value) return
    const sel   = avaliacoes.value.find(a => a.id === avaliacaoId.value)
    const nome  = sel?.nome || 'Avaliação'
    const label = (criterio.value === 'realizaram') ? 'Realizadas' : 'Não Realizadas'
    const fmt = (d) => {
    const dt = new Date(d); const dd = String(dt.getDate()).padStart(2,'0')
    const mm = String(dt.getMonth()+1).padStart(2,'0'); const yy = dt.getFullYear()
    return `${dd}/${mm}/${yy}`
    }
    const sub = (sel?.data_inicio && sel?.data_fim)
    ? `Período de avaliação: ${fmt(sel.data_inicio)} a ${fmt(sel.data_fim)}`
    : ''

    router.push({
        path: '/gestao/relatorios/print/avaliacao-operacional',
        query: {
            title:    `Avaliações ${label} - ${nome}`,
            subtitle: sub,
            avaliacao_id: String(avaliacaoId.value),
            criterio: criterio.value,
            avaliacao_nome: nome
        }
    })
}

// Quando em modo print, busca com base nos parâmetros da URL
onMounted(async () => {
  if (!props.printMode) {
    await carregarAvaliacoes()
    return
  }
  const avaliacao_id = route.query?.avaliacao_id
  const crit = route.query?.criterio
  if (!avaliacao_id || !crit) return

  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/avaliacao', {
      params: { avaliacao_id, criterio: crit }
    })
    data.value = res.data.data || {}
  } catch (e) {
    console.error('Erro ao buscar Avaliação Operacional:', e)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.segmented { display:inline-flex; border:1px solid #ddd; border-radius:10px; overflow:hidden; }
.seg-btn { padding:.5rem .9rem; border:0; background:#f7f7f7; cursor:pointer; }
.seg-btn.active { background:white; font-weight:600; box-shadow: inset 0 -2px 0 #00000022; }

/* Tabelas com largura fixa e colunas iguais, seguindo padrão existente */
table { table-layout: fixed; width: 100%; font-size: .75rem; }
th, td { width: 50%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>